import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Calendar, 
  PieChart, 
  Target, 
  Lightbulb, 
  TrendingUp, 
  Shield,
  ArrowRight,
  CheckCircle,
  Star
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ecaa2978-2f20-40e3-8453-4248c3146dce/artifacts/mfi4q0wb_7710DE4F-B183-47BF-A0BF-17D901748F49.png";

const features = [
  {
    icon: Calendar,
    title: "See all your bills in one calendar",
    description: "Know exactly when money goes out and plan ahead with confidence"
  },
  {
    icon: PieChart,
    title: "Know what you have left after bills",
    description: "See your safe-to-spend amount at a glance, updated in real-time"
  },
  {
    icon: Target,
    title: "Track your path to debt-free",
    description: "Watch your progress and see your projected payoff date"
  },
  {
    icon: Lightbulb,
    title: "Get simple guidance on what to do next",
    description: "Helpful suggestions based on your actual financial situation"
  },
  {
    icon: TrendingUp,
    title: "See how extra payments change your future",
    description: "Simulate different scenarios to find what works for you"
  },
  {
    icon: Shield,
    title: "Know before you run short",
    description: "Get alerts when upcoming bills might stretch your budget"
  }
];

const testimonials = [
  {
    name: "Sarah T.",
    text: "Finally, I can open my banking app without anxiety. Path2Plenty makes it so clear.",
    rating: 5
  },
  {
    name: "James M.",
    text: "The calendar view changed how I think about my monthly expenses. Simple but powerful.",
    rating: 5
  },
  {
    name: "Emma L.",
    text: "Knowing my safe-to-spend amount every week has been a game changer for my budget.",
    rating: 5
  }
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-p2p-bg">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-p2p-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img src={LOGO_URL} alt="Path2Plenty" className="h-14 w-auto" />
            </Link>
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                className="text-p2p-muted hover:text-p2p-teal hover:bg-p2p-teal/5"
                onClick={() => navigate('/auth')}
                data-testid="login-btn"
              >
                Log In
              </Button>
              <Button 
                className="btn-gold rounded-full px-6"
                onClick={() => navigate('/auth?mode=register')}
                data-testid="get-started-btn"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-p2p-teal/10 border border-p2p-teal/20">
                <Shield className="w-4 h-4 text-p2p-teal" />
                <span className="text-sm text-p2p-teal font-medium">Built for real life</span>
              </div>
              
              <h1 className="font-manrope text-4xl sm:text-5xl lg:text-6xl font-bold text-p2p-teal leading-tight">
                Know where your money is going{" "}
                <span className="text-p2p-gold">— and if you&apos;ll be okay this week</span>
              </h1>
              
              <p className="text-lg text-p2p-muted max-w-xl">
                Path2Plenty helps you track bills, reduce debt, and know what you can safely spend — without judgement or confusion.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  className="btn-gold rounded-full px-8 py-6 text-lg font-semibold"
                  onClick={() => navigate('/auth?mode=register')}
                  data-testid="hero-cta-btn"
                >
                  Start Your Journey <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  variant="outline"
                  className="rounded-full px-8 py-6 text-lg border-p2p-teal/30 text-p2p-teal hover:bg-p2p-teal/5"
                  onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                  data-testid="learn-more-btn"
                >
                  Learn More
                </Button>
              </div>
              
              <div className="pt-4">
                <p className="text-sm text-p2p-muted flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Designed for people who want control, not complexity
                </p>
              </div>
            </div>
            
            <div className="relative">
              <div className="light-card p-8 rounded-3xl shadow-xl animate-fade-in">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="font-manrope text-xl font-semibold text-p2p-teal">Financial Overview</h3>
                    <span className="px-3 py-1 rounded-full bg-p2p-success/10 text-p2p-success text-sm font-medium">Healthy</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl bg-p2p-surface-hover">
                      <p className="text-sm text-p2p-muted mb-1">Total Debt</p>
                      <p className="text-2xl font-mono font-bold text-p2p-error">£8,450</p>
                    </div>
                    <div className="p-4 rounded-xl bg-p2p-surface-hover">
                      <p className="text-sm text-p2p-muted mb-1">Monthly Income</p>
                      <p className="text-2xl font-mono font-bold text-p2p-success">£3,200</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-p2p-muted">Debt Payoff Progress</span>
                      <span className="text-p2p-gold font-medium">67%</span>
                    </div>
                    <div className="h-3 bg-p2p-surface-hover rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-p2p-gold to-p2p-gold-hover rounded-full" style={{ width: '67%' }} />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-p2p-gold/10 border border-p2p-gold/20">
                    <Lightbulb className="w-5 h-5 text-p2p-gold flex-shrink-0" />
                    <p className="text-sm text-p2p-teal">Pay £50 extra this month to save £120 in interest!</p>
                  </div>
                </div>
              </div>
              
              {/* Floating element */}
              <div className="absolute -top-4 -right-4 light-card p-4 rounded-2xl shadow-lg animate-fade-in animation-delay-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-p2p-success/10 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-p2p-success" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-p2p-teal">Bill Paid</p>
                    <p className="text-xs text-p2p-muted">Netflix - £14.99</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-p2p-teal mb-4">
              Everything You Need to Manage Your Finances
            </h2>
            <p className="text-lg text-p2p-muted max-w-2xl mx-auto">
              Powerful features designed to help you understand, plan, and improve your financial health
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="light-card light-card-hover p-6 rounded-2xl opacity-0 animate-slide-up"
                style={{ animationDelay: `${index * 100}ms`, animationFillMode: 'forwards' }}
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-p2p-teal/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-p2p-teal" />
                </div>
                <h3 className="font-manrope text-xl font-semibold text-p2p-teal mb-2">{feature.title}</h3>
                <p className="text-p2p-muted">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-p2p-teal/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-p2p-teal mb-4">
              Loved by Thousands
            </h2>
            <p className="text-lg text-p2p-muted">Join our community of financially empowered users</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={testimonial.name}
                className="light-card p-6 rounded-2xl"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-p2p-gold text-p2p-gold" />
                  ))}
                </div>
                <p className="text-p2p-teal mb-4">&quot;{testimonial.text}&quot;</p>
                <p className="text-sm text-p2p-muted font-medium">{testimonial.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-p2p-teal mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-lg text-p2p-muted mb-8">
            Join thousands of users who have taken control of their financial future
          </p>
          <Button 
            className="btn-gold rounded-full px-12 py-6 text-lg font-semibold"
            onClick={() => navigate('/auth?mode=register')}
            data-testid="final-cta-btn"
          >
            Get Started Free <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <p className="text-sm text-p2p-muted mt-4">No credit card required • Free forever plan available</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-p2p-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Path2Plenty" className="h-8 w-auto" />
            </div>
            <p className="text-sm text-p2p-muted">© 2026 Path2Plenty. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
